import { create } from 'zustand';
import type { AspectRatioOption, CropData, Area, Point } from '../types';

export interface CropState {
  // Image state
  originalWidth: number;
  originalHeight: number;
  isImageLoaded: boolean;

  // Crop selection state for react-easy-crop
  cropPosition: Point;
  zoom: number;
  croppedArea: Area | null;
  croppedAreaPixels: Area | null;
  aspectRatio: AspectRatioOption;
  aspectRatioValue: number | null;

  // UI state
  isSubmitting: boolean;
  error: string | null;

  // Actions
  setOriginalDimensions: (width: number, height: number) => void;
  setImageLoaded: (loaded: boolean) => void;
  setCropPosition: (position: Point) => void;
  setZoom: (zoom: number) => void;
  setCroppedArea: (area: Area | null, areaPixels: Area | null) => void;
  setAspectRatio: (option: AspectRatioOption) => void;
  setSubmitting: (submitting: boolean) => void;
  setError: (error: string | null) => void;
  resetState: () => void;

  // Computed
  hasSelection: () => boolean;
  getCropData: () => CropData;
}

const initialState = {
  // Image state
  originalWidth: 0,
  originalHeight: 0,
  isImageLoaded: false,

  // Crop selection state
  cropPosition: { x: 0, y: 0 },
  zoom: 1,
  croppedArea: null,
  croppedAreaPixels: null,
  aspectRatio: 'original' as AspectRatioOption,
  aspectRatioValue: null,

  // UI state
  isSubmitting: false,
  error: null,
};

export const useCropStore = create<CropState>((set, get) => ({
  ...initialState,

  // Actions
  setOriginalDimensions: (width, height) =>
    set({
      originalWidth: width,
      originalHeight: height,
      aspectRatioValue: get().aspectRatio === 'original' ? width / height : get().aspectRatioValue,
    }),

  setImageLoaded: (loaded) => set({ isImageLoaded: loaded }),

  // Store crop position and zoom
  setCropPosition: (position) => set({ cropPosition: position }),
  setZoom: (zoom) => set({ zoom }),
  setCroppedArea: (area, areaPixels) => set({ croppedArea: area, croppedAreaPixels: areaPixels }),

  setAspectRatio: (option) => {
    let aspectRatioValue: number | null = null;

    switch (option) {
      case '1:1': {
        aspectRatioValue = 1;
        break;
      }
      case '16:9': {
        aspectRatioValue = 16 / 9;
        break;
      }
      case '9:16': {
        aspectRatioValue = 9 / 16;
        break;
      }
      case 'original': {
        const { originalWidth, originalHeight } = get();
        aspectRatioValue = originalWidth && originalHeight ? originalWidth / originalHeight : null;
        break;
      }
    }

    // Reset crop position, zoom, and crop area when aspect ratio changes
    set({
      aspectRatio: option,
      aspectRatioValue,
      cropPosition: { x: 0, y: 0 },
      zoom: 1,
      croppedArea: null,
      croppedAreaPixels: null,
    });
  },

  setSubmitting: (submitting) => set({ isSubmitting: submitting }),

  setError: (error) => set({ error }),

  resetState: () => set(initialState),

  // Computed values
  hasSelection: () => {
    const { croppedArea } = get();
    return !!croppedArea && croppedArea.width > 0 && croppedArea.height > 0;
  },

  // Convert react-easy-crop output to API format
  getCropData: () => {
    const { croppedAreaPixels, originalWidth, originalHeight } = get();

    if (!croppedAreaPixels) {
      return {
        x: 0,
        y: 0,
        width: originalWidth,
        height: originalHeight,
        original_width: originalWidth,
        original_height: originalHeight,
        regenerate_variants: true,
      };
    }

    // react-easy-crop already provides pixel values
    return {
      x: Math.round(croppedAreaPixels.x),
      y: Math.round(croppedAreaPixels.y),
      width: Math.round(croppedAreaPixels.width),
      height: Math.round(croppedAreaPixels.height),
      original_width: originalWidth,
      original_height: originalHeight,
      regenerate_variants: true,
    };
  },
}));
